import React, { useCallback, useMemo, useRef, useState } from 'react';
import type { InvoiceProps } from '@goodfynd/checkout';
import { Invoice } from '@goodfynd/checkout';
import { StyledFlexCenter } from '@goodfynd/styles';
import type { CartOrder } from '@goodfynd/types';
import { LoadingRegister } from '@goodfynd/ui';
import type { GetServerSidePropsContext, GetServerSidePropsResult } from 'next';
import Head from 'next/head';
import Custom500 from 'pages/500';
import type { VendorItem } from 'types/shared';

import config from '../../../config';
import { useOrderApi, useVendorApi } from '../../../hooks/api';
import { useInvoicePayment } from '../../../hooks/useInvoicePayment';
import { OrderApi, VendorApi } from '../../../services/api';
import { renderPrice } from '../../../utils/number-util';
import Custom404 from '../../404';

type InvoicePageProps =
  | {
      hasError?: never;
      invoice: CartOrder;
      merchant?: VendorItem;
    }
  | {
      hasError: true;
      invoice?: never;
      merchant?: never;
    };

const orderApiServer = new OrderApi();
const vendorApiServer = new VendorApi();

function InvoicePage({ hasError, invoice, ...props }: InvoicePageProps) {
  const orderApi = useOrderApi();
  const vendorApi = useVendorApi();
  const [busy, setBusy] = useState(true);
  const [isAcceptingQuote, setIsAcceptingQuote] = useState(false);
  const [merchant, setMerchant] = React.useState<VendorItem | undefined>(
    props.merchant
  );
  const {
    addTipMutation: { mutateAsync: addTip, isLoading: isAdjustingTip },
    finalizeMutation: { mutateAsync: finalizeInvoice, isLoading: isFinalizing },
  } = useInvoicePayment();

  const initialTip = useRef(invoice?.tip);
  React.useEffect(() => {
    if (!hasError && !props.merchant?.id) {
      (async () => {
        const { vendor } = await vendorApi.get(invoice.merchant.id);
        setMerchant(vendor);
        setBusy(false);
      })();
    } else {
      setBusy(false);
    }
  }, [hasError, invoice?.merchant.id, props.merchant?.id, vendorApi]);

  const onAccept = useMemo(
    () =>
      invoice?.type === 'quote'
        ? async (transactionId: string) => {
            const confirmed = confirm(
              `Are you sure you want to accept this quote? If so, an invoice will be created and opened for payment`
            );

            if (confirmed) {
              try {
                setIsAcceptingQuote(true);
                await orderApi.acceptQuote(transactionId);
                window.location.reload();
              } catch (error: unknown) {
                console.error(error);
                const errorMessage =
                  error && typeof error === 'object' && 'message' in error
                    ? String((error as { message: unknown }).message)
                    : 'An unknown error has occurred.';
                alert(errorMessage);
                setIsAcceptingQuote(false);
              }
            }
          }
        : undefined,
    [invoice?.type, orderApi]
  );

  const onPay = useMemo(() => {
    if (invoice?.type !== 'invoice') {
      return undefined;
    }

    if (!invoice.isFinalized && invoice.invoiceNumber) {
      // TODO: Remove after all legacy invoices are paid or expired
      return async (
        invoice: InvoiceProps['invoice'],
        tip = Number(undefined)
      ) => {
        if ((!tip && !initialTip.current) || tip === initialTip.current) {
          window.open(invoice.paymentUrl, '_self');
          return;
        }

        try {
          const { order } = await addTip({ id: invoice.id, tip: tip || 0 });
          window.open(order.paymentUrl || invoice.paymentUrl, '_self');
        } catch {
          window.open(invoice.paymentUrl, '_self');
        }
      };
    }

    if (invoice.isFinalized) {
      return async (invoice: InvoiceProps['invoice']) => {
        window.open(invoice.paymentUrl, '_self');
      };
    }

    return async (invoice: InvoiceProps['invoice'], tip = 0) => {
      const confirmed = confirm(
        `This will finalize the invoice for the total amount of: ${renderPrice(
          invoice.total + tip
        )}

***This cannot be reversed.`
      );
      if (!confirmed) {
        return;
      }

      const { order } = await finalizeInvoice({ id: invoice.id, tip });
      window.open(order.paymentUrl, '_self');
    };
  }, [
    addTip,
    finalizeInvoice,
    invoice?.invoiceNumber,
    invoice?.isFinalized,
    invoice?.type,
  ]);

  const getTitle = useCallback(() => {
    if (!invoice || !merchant) {
      return 'Review Invoice';
    }

    const merchantName = merchant.uniqueName || merchant.name;
    if (invoice.type === 'quote') {
      return `Review Quote from ${merchantName}`;
    }

    if (!invoice.invoiceNumber) {
      return `Review Invoice from ${merchantName}`;
    }

    if (
      ['paid', 'paid_external', 'partially_refunded'].includes(
        invoice.paymentStatus
      )
    ) {
      return `Paid ${merchantName} Invoice #${invoice.invoiceNumber}`;
    }

    if (['refunded', 'voided'].includes(invoice.paymentStatus)) {
      return `Canceled ${merchantName} Invoice #${invoice.invoiceNumber}`;
    }

    return `Pay ${merchantName} Invoice #${invoice.invoiceNumber}`;
  }, [invoice, merchant]);

  if (busy) {
    return <LoadingRegister />;
  }

  if (hasError) {
    return <Custom500 />;
  }

  if (!merchant) {
    return <Custom404 />;
  }

  return (
    <>
      <Head>
        <title>{getTitle()}</title>
        <meta content="noindex, nofollow" name="robots" />
      </Head>

      <StyledFlexCenter
        css={{
          backgroundColor: 'var(--BackgroundBase0)',
          padding: 64,
          '@smMax': { padding: '48px 0' },
        }}
      >
        <Invoice
          busy={isAcceptingQuote || isAdjustingTip || isFinalizing}
          collectPayment
          collectTip={invoice.type === 'invoice'}
          logoEnvironment={config.env.NAME_LOWER}
          onAccept={onAccept}
          onPay={onPay}
          showColorSchemeToggle
          {...{
            // businessEmail: merchant.managerEmail,
            businessImage: merchant.image,
            businessPhone: merchant.phone,
            invoice,
          }}
        />
      </StyledFlexCenter>
    </>
  );
}

InvoicePage.getLayout = function getLayout(page: React.ReactElement) {
  return page;
};

// Disable static rendering
export const getServerSideProps = async (
  context: GetServerSidePropsContext
): Promise<GetServerSidePropsResult<InvoicePageProps>> => {
  const { id } = context.query;

  let invoice: CartOrder;
  let merchant: VendorItem | undefined;
  try {
    const { order } = await orderApiServer.getOrder(id as string);
    invoice = order;
  } catch (error) {
    console.error(error);
    return { props: { hasError: true } };
  }

  try {
    const { vendor } = await vendorApiServer.get(invoice.merchant.id);
    merchant = vendor;
  } catch (error) {
    console.debug(error);
  } finally {
    return { props: merchant ? { invoice, merchant } : { invoice } };
  }
};

export default InvoicePage;
